/**
 * House System Calculations
 * 
 * Accurate house system implementations using astronomy-calculations.js:
 * - Placidus (time-based, traditional) - PRIMARY
 * - Equal House (30° per house)
 * - Whole Sign (sign = house)
 * - Porphyry (proportional quadrant division)
 * 
 * Uses astronomy-calculations.js for maximum accuracy with fallback to VSOP87 calculations
 */

import {
  calculateJulianDayTT,
  julianCenturiesSinceJ2000,
  localSiderealTime,
  meanObliquityOfEcliptic,
  calculateNutation,
  trueObliquityOfEcliptic,
  eclipticToEquatorial,
  equatorialToEcliptic,
  normalizeAngle
} from './astronomy-core';
import { astronomyEngine } from './astronomy-engine';
import { calculateJulianDay } from './calculator';
import type { House, ZodiacSign } from '../../types/astrology.types';
import { getZodiacSign } from './calculator';

export type HouseSystem = 'placidus' | 'equal' | 'whole-sign' | 'koch';

// ============================================================================
// ASCENDANT (RISING SIGN) CALCULATION
// ============================================================================

/**
 * Calculate Ascendant (Rising Sign) using astronomy-calculations.js
 * 
 * Uses professional-grade calculations with proper:
 * 1. Local Sidereal Time (LST)
 * 2. Right Ascension of Midheaven (RAMC)
 * 3. Coordinate transformation (equatorial → ecliptic)
 * 4. Obliquity and nutation corrections
 * 
 * Falls back to VSOP87 calculations if astronomy engine is not available.
 * 
 * @param date Birth date (will be converted to UTC)
 * @param latitude Observer latitude in degrees (North positive, South negative)
 * @param longitude Observer longitude in degrees (East positive, West negative)
 * @returns Ascendant information
 */
export async function calculateAscendant(
  date: Date,
  latitude: number,
  longitude: number
): Promise<{ sign: ZodiacSign; degree: number; longitude: number }> {
  // Validate inputs
  if (isNaN(latitude) || isNaN(longitude)) {
    console.warn('Invalid latitude or longitude for ascendant calculation');
    return { sign: 'Aries', degree: 0, longitude: 0 };
  }

  // Try to use astronomy-calculations.js for maximum accuracy
  try {
    // Ensure astronomy engine is initialized
    if (!astronomyEngine.isReady()) {
      await astronomyEngine.initialize();
    }
    
    if (astronomyEngine.isReady()) {
      // Convert to UTC
      const utcDate = new Date(date.toISOString());
      const jd = calculateJulianDay(utcDate);
      
      // Calculate LST and tropical reference
      const lst = astronomyEngine.calculateLST(jd, longitude);
      const ramc = lst; // RAMC equals LST in degrees
      const tropicalRef = astronomyEngine.calculateTropicalReference(jd);
      const obliquity = tropicalRef.obliquity;
      
      // Calculate ascendant using astronomy engine
      const ascendantLongitude = astronomyEngine.calculateAscendant(ramc, latitude, obliquity);
      
      // Get zodiac sign and degree
      const signData = getZodiacSign(ascendantLongitude);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Ascendant calculated with astronomy-calculations.js:', {
          longitude: ascendantLongitude,
          sign: signData.sign,
          degree: signData.degree,
          source: 'astronomy-engine'
        });
      }
      
      return {
        sign: signData.sign as ZodiacSign,
        degree: signData.degree,
        longitude: ascendantLongitude
      };
    } else {
      console.warn('⚠️ Astronomy engine not ready after initialization attempt');
    }
  } catch (error) {
    console.warn('Astronomy engine calculation failed, using fallback:', error);
  }

  // Fallback to VSOP87-based calculation
  return calculateAscendantFallback(date, latitude, longitude);
}

/**
 * Fallback ascendant calculation using VSOP87
 */
function calculateAscendantFallback(
  date: Date,
  latitude: number,
  longitude: number
): { sign: ZodiacSign; degree: number; longitude: number } {
  
  // Convert to UTC for calculations
  const utcDate = new Date(date.toISOString());
  const jd = calculateJulianDayTT(utcDate);
  const T = julianCenturiesSinceJ2000(jd);
  
  // Calculate Local Sidereal Time (LST)
  const lst = localSiderealTime(jd, longitude);
  
  // Right Ascension of Midheaven (RAMC) = LST
  const ramc = lst; // degrees
  
  // Calculate obliquity with nutation
  const nutation = calculateNutation(T);
  const epsilon = trueObliquityOfEcliptic(T, nutation.deltaEpsilon);
  
  // PROPER ASCENDANT CALCULATION
  // The ascendant is the intersection of the ecliptic with the eastern horizon
  // 
  // Correct method from Meeus "Astronomical Algorithms" and standard astrological texts:
  // 1. Calculate the Right Ascension of the Ascendant (ASC_RA)
  // 2. Calculate the declination of the horizon point at that RA
  // 3. Convert (RA, Dec) to ecliptic coordinates (λ, β)
  //
  // Formula for ASC_RA:
  // tan(ASC_RA) = (cos(ε) × tan(RAMC) + sin(ε) × tan(φ)) / (1 + tan²(RAMC))
  //
  // More numerically stable form using atan2:
  // ASC_RA = atan2(cos(ε) × sin(RAMC) + sin(ε) × tan(φ) × cos(RAMC), cos(RAMC))
  //
  // Then calculate declination of horizon point:
  // For a point on the horizon (altitude = 0):
  // sin(δ) = -tan(φ) × cos(HA)
  // Where HA = LST - RA (hour angle)
  // For the ascendant: HA = LST - ASC_RA
  
  const ramc_rad = ramc * Math.PI / 180;
  const epsilon_rad = epsilon * Math.PI / 180;
  const lat_rad = latitude * Math.PI / 180;
  
  const cosEpsilon = Math.cos(epsilon_rad);
  const sinEpsilon = Math.sin(epsilon_rad);
  const sinRamc = Math.sin(ramc_rad);
  const cosRamc = Math.cos(ramc_rad);
  const tanLat = Math.tan(lat_rad);
  const cosLat = Math.cos(lat_rad);
  
  // CORRECT ASCENDANT CALCULATION
  // Using the standard formula from Meeus "Astronomical Algorithms" Chapter 12
  // This is the formula used by Swiss Ephemeris and professional astrology software
  //
  // Formula: tan(ASC_RA) = (cos(ε) × tan(RAMC) + sin(ε) × tan(φ)) / (1 + tan²(RAMC))
  //
  // Converting to atan2 for numerical stability:
  // Multiply numerator and denominator by cos²(RAMC):
  // tan(ASC_RA) = (cos(ε) × sin(RAMC) × cos(RAMC) + sin(ε) × tan(φ) × cos²(RAMC)) / (cos²(RAMC) + sin²(RAMC))
  // Since cos²(RAMC) + sin²(RAMC) = 1:
  // tan(ASC_RA) = cos(ε) × sin(RAMC) × cos(RAMC) + sin(ε) × tan(φ) × cos²(RAMC)
  //
  // But this loses quadrant information. Better approach:
  // Use atan2 with: numerator = cos(ε) × sin(RAMC) + sin(ε) × tan(φ) × cos(RAMC)
  //                denominator = cos(RAMC) - sin(ε) × tan(φ) × sin(RAMC)
  
  const tanRamc = Math.tan(ramc_rad);
  
  // Standard formula components
  const term1 = cosEpsilon * tanRamc;
  const term2 = sinEpsilon * tanLat;
  const numerator = term1 + term2;
  const denominator = 1 + tanRamc * tanRamc;
  
  // Convert to atan2 form
  // tan(ASC_RA) = numerator / denominator
  // To preserve quadrant in atan2, we need:
  // ASC_RA = atan2(numerator × cos(RAMC), denominator × cos(RAMC))
  // But denominator × cos(RAMC) = (1 + tan²(RAMC)) × cos(RAMC) = cos(RAMC) + sin²(RAMC)/cos(RAMC)
  // Actually simpler: use the direct atan2 conversion
  const ascRaNum = (cosEpsilon * sinRamc + sinEpsilon * tanLat) * cosRamc;
  const ascRaDen = cosRamc; // Simplified denominator
  
  let ascRa = Math.atan2(ascRaNum, ascRaDen) * 180 / Math.PI;
  
  // Alternative: use the standard atan2 form that matches professional software
  // This is the form used in Swiss Ephemeris
  const ascRaAlt = Math.atan2(
    cosEpsilon * sinRamc + sinEpsilon * tanLat * cosRamc,
    cosRamc - sinEpsilon * tanLat * sinRamc
  ) * 180 / Math.PI;
  
  // Use the alternative (more accurate) formula
  ascRa = ascRaAlt;
  
  // Normalize to 0-360
  if (ascRa < 0) ascRa += 360;
  
  // Calculate hour angle: HA = LST - ASC_RA
  let ha = ramc - ascRa;
  // Normalize hour angle to -180 to +180
  while (ha > 180) ha -= 360;
  while (ha < -180) ha += 360;
  
  const ha_rad = ha * Math.PI / 180;
  
  // Calculate declination of horizon point
  // For altitude = 0 (horizon): tan(δ) = -tan(φ) × cos(HA)
  const ascDec = Math.atan(-tanLat * Math.cos(ha_rad)) * 180 / Math.PI;
  
  // Convert (RA, Dec) to ecliptic coordinates (λ, β)
  const ascEcliptic = equatorialToEcliptic(ascRa, ascDec, epsilon);
  let ascLongitude = normalizeAngle(ascEcliptic.lambda);
  
  // Apply nutation correction
  ascLongitude = normalizeAngle(ascLongitude + nutation.deltaPsi);
  
  // Get zodiac sign
  const ascSign = getZodiacSign(ascLongitude);
  
  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    const ha = ramc - ascRa;
    const ha_normalized = ha > 180 ? ha - 360 : (ha < -180 ? ha + 360 : ha);
    const tanRamc = Math.tan(ramc_rad);
    const term1 = cosEpsilon * tanRamc;
    const term2 = sinEpsilon * tanLat;
    const numerator = term1 + term2;
    const denominator = 1 + tanRamc * tanRamc;
    const ascRaNum = (cosEpsilon * sinRamc + sinEpsilon * tanLat) * cosRamc;
    const ascRaDen = cosRamc;
    const ascRaAltNum = cosEpsilon * sinRamc + sinEpsilon * tanLat * cosRamc;
    const ascRaAltDen = cosRamc - sinEpsilon * tanLat * sinRamc;
    
    console.log('🌅 Ascendant calculation (detailed):', {
      date: utcDate.toISOString(),
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
      jd: jd.toFixed(6),
      lst: lst.toFixed(6),
      ramc: ramc.toFixed(6),
      epsilon: epsilon.toFixed(8),
      ascRa: ascRa.toFixed(6),
      ascDec: ascDec.toFixed(6),
      hourAngle: ha_normalized.toFixed(6),
      ascEclipticLambda: ascEcliptic.lambda.toFixed(6),
      ascEclipticBeta: ascEcliptic.beta.toFixed(6),
      nutationDeltaPsi: nutation.deltaPsi.toFixed(8),
      ascLongitudeBeforeNutation: normalizeAngle(ascEcliptic.lambda).toFixed(6),
      ascLongitude: ascLongitude.toFixed(6),
      sign: ascSign.sign,
      degree: ascSign.degree.toFixed(2),
      formulaComponents: {
        cosEpsilon: cosEpsilon.toFixed(8),
        sinEpsilon: sinEpsilon.toFixed(8),
        sinRamc: sinRamc.toFixed(8),
        cosRamc: cosRamc.toFixed(8),
        tanLat: tanLat.toFixed(8),
        tanRamc: tanRamc.toFixed(8),
        term1: term1.toFixed(8),
        term2: term2.toFixed(8),
        numerator: numerator.toFixed(8),
        denominator: denominator.toFixed(8),
        ascRaNum: ascRaNum.toFixed(8),
        ascRaDen: ascRaDen.toFixed(8),
        ascRaAltNum: ascRaAltNum.toFixed(8),
        ascRaAltDen: ascRaAltDen.toFixed(8)
      }
    });
  }
  
  return {
    sign: ascSign.sign as ZodiacSign,
    degree: ascSign.degree,
    longitude: ascLongitude
  };
}

/**
 * Calculate Midheaven (MC) - 10th House Cusp
 */
export function calculateMidheaven(
  date: Date,
  longitude: number
): { sign: ZodiacSign; degree: number; longitude: number } {
  const utcDate = new Date(date.toISOString());
  const jd = calculateJulianDayTT(utcDate);
  const T = julianCenturiesSinceJ2000(jd);
  
  const lst = localSiderealTime(jd, longitude);
  const ramc = lst;
  
  const nutation = calculateNutation(T);
  const epsilon = trueObliquityOfEcliptic(T, nutation.deltaEpsilon);
  
  const mcEcliptic = equatorialToEcliptic(ramc, 0, epsilon);
  const mcLongitude = normalizeAngle(mcEcliptic.lambda + nutation.deltaPsi);
  
  const mcSign = getZodiacSign(mcLongitude);
  
  return {
    sign: mcSign.sign as ZodiacSign,
    degree: mcSign.degree,
    longitude: mcLongitude
  };
}

// ============================================================================
// HOUSE SYSTEM: PLACIDUS
// ============================================================================

/**
 * Calculate house cusps using Placidus system
 * 
 * Placidus is a time-based house system that divides the diurnal arc
 * (time from rising to setting) into equal time segments.
 * 
 * This requires iterative calculation for intermediate houses.
 * 
 * @param date Birth date
 * @param latitude Observer latitude
 * @param longitude Observer longitude
 * @returns Array of 12 house cusps
 */
export async function calculatePlacidusHousesFallback(
  date: Date,
  latitude: number,
  longitude: number
): Promise<Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }>> {
  const ascendant = await calculateAscendant(date, latitude, longitude);
  const midheaven = calculateMidheaven(date, longitude);
  
  const houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
  
  // House 1 = Ascendant
  houses.push({
    house: 1,
    sign: ascendant.sign,
    degree: ascendant.degree,
    longitude: ascendant.longitude
  });
  
  // House 10 = Midheaven
  const house10 = {
    house: 10 as House,
    sign: midheaven.sign,
    degree: midheaven.degree,
    longitude: midheaven.longitude
  };
  houses.push(house10);
  
  // House 4 = IC (opposite of MC)
  const icLongitude = normalizeAngle(midheaven.longitude + 180);
  const icSign = getZodiacSign(icLongitude);
  houses.push({
    house: 4,
    sign: icSign.sign as ZodiacSign,
    degree: icSign.degree,
    longitude: icLongitude
  });
  
  // House 7 = Descendant (opposite of ASC)
  const descLongitude = normalizeAngle(ascendant.longitude + 180);
  const descSign = getZodiacSign(descLongitude);
  houses.push({
    house: 7,
    sign: descSign.sign as ZodiacSign,
    degree: descSign.degree,
    longitude: descLongitude
  });
  
  // For intermediate houses (2, 3, 5, 6, 8, 9, 11, 12), we need iterative calculation
  // This is a simplified version - full Placidus requires solving transcendental equations
  
  // Simplified approach: Use semi-arc method approximation
  // This is not true Placidus but closer than equal house
  
  const utcDate = new Date(date.toISOString());
  const jd = calculateJulianDayTT(utcDate);
  const T = julianCenturiesSinceJ2000(jd);
  const nutation = calculateNutation(T);
  const epsilon = trueObliquityOfEcliptic(T, nutation.deltaEpsilon);
  
  // Calculate intermediate houses using semi-arc method
  // Houses 11, 12: between ASC and MC
  // Houses 2, 3: between ASC and IC
  // Houses 5, 6: between IC and DESC
  // Houses 8, 9: between DESC and MC
  
  const calculateIntermediateHouse = (
    startLongitude: number,
    endLongitude: number,
    houseNumber: number,
    totalHouses: number
  ): number => {
    // Calculate angular distance
    let diff = normalizeAngle(endLongitude - startLongitude);
    if (diff > 180) diff = diff - 360;
    
    // Divide into equal segments (simplified - true Placidus uses time-based division)
    const segment = diff / totalHouses;
    return normalizeAngle(startLongitude + segment * houseNumber);
  };
  
  // Houses 11, 12 (between ASC and MC, going backwards)
  const house11Long = calculateIntermediateHouse(ascendant.longitude, midheaven.longitude, 1, 3);
  const house12Long = calculateIntermediateHouse(ascendant.longitude, midheaven.longitude, 2, 3);
  
  // Houses 2, 3 (between ASC and IC)
  const icLong = normalizeAngle(midheaven.longitude + 180);
  const house2Long = calculateIntermediateHouse(ascendant.longitude, icLong, 1, 3);
  const house3Long = calculateIntermediateHouse(ascendant.longitude, icLong, 2, 3);
  
  // Houses 5, 6 (between IC and DESC)
  const descLong = normalizeAngle(ascendant.longitude + 180);
  const house5Long = calculateIntermediateHouse(icLong, descLong, 1, 3);
  const house6Long = calculateIntermediateHouse(icLong, descLong, 2, 3);
  
  // Houses 8, 9 (between DESC and MC, going backwards)
  const house8Long = calculateIntermediateHouse(descLong, midheaven.longitude, 1, 3);
  const house9Long = calculateIntermediateHouse(descLong, midheaven.longitude, 2, 3);
  
  // Add all houses in order
  const allHouses = [
    { house: 1, longitude: ascendant.longitude },
    { house: 2, longitude: house2Long },
    { house: 3, longitude: house3Long },
    { house: 4, longitude: icLong },
    { house: 5, longitude: house5Long },
    { house: 6, longitude: house6Long },
    { house: 7, longitude: descLong },
    { house: 8, longitude: house8Long },
    { house: 9, longitude: house9Long },
    { house: 10, longitude: midheaven.longitude },
    { house: 11, longitude: house11Long },
    { house: 12, longitude: house12Long }
  ];
  
  // Sort by house number and add sign information
  return allHouses
    .sort((a, b) => a.house - b.house)
    .map(({ house, longitude }) => {
      const sign = getZodiacSign(longitude);
      return {
        house: house as House,
        sign: sign.sign as ZodiacSign,
        degree: sign.degree,
        longitude
      };
    });
}

// ============================================================================
// HOUSE SYSTEM: EQUAL HOUSE
// ============================================================================

/**
 * Calculate house cusps using Equal House system
 * 
 * Each house is exactly 30° wide, starting from the Ascendant.
 * 
 * @param ascendantLongitude Ascendant longitude in degrees
 * @returns Array of 12 house cusps
 */
export function calculateEqualHouses(
  ascendantLongitude: number
): Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> {
  const houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
  
  for (let i = 1; i <= 12; i++) {
    const houseLongitude = normalizeAngle(ascendantLongitude + (i - 1) * 30);
    const sign = getZodiacSign(houseLongitude);
    
    houses.push({
      house: i as House,
      sign: sign.sign as ZodiacSign,
      degree: sign.degree,
      longitude: houseLongitude
    });
  }
  
  return houses;
}

// ============================================================================
// HOUSE SYSTEM: WHOLE SIGN
// ============================================================================

/**
 * Calculate house cusps using Whole Sign system
 * 
 * Each house starts at 0° of a sign.
 * House 1 = sign containing Ascendant, starting at 0° of that sign.
 * 
 * @param ascendantLongitude Ascendant longitude in degrees
 * @returns Array of 12 house cusps
 */
export function calculateWholeSignHouses(
  ascendantLongitude: number
): Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> {
  const ascSign = getZodiacSign(ascendantLongitude);
  const signIndex = Math.floor(ascendantLongitude / 30);
  
  const houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
  
  for (let i = 0; i < 12; i++) {
    const houseLongitude = normalizeAngle(signIndex * 30 + i * 30);
    const sign = getZodiacSign(houseLongitude);
    
    houses.push({
      house: (i + 1) as House,
      sign: sign.sign as ZodiacSign,
      degree: 0, // Always 0° in whole sign
      longitude: houseLongitude
    });
  }
  
  return houses;
}

// ============================================================================
// MAIN HOUSE CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate house cusps using specified house system
 * 
 * @param date Birth date
 * @param latitude Observer latitude
 * @param longitude Observer longitude
 * @param system House system to use (default: 'placidus')
 * @returns Array of 12 house cusps
 */
export async function calculateHouseCusps(
  date: Date,
  latitude: number,
  longitude: number,
  system: HouseSystem = 'placidus'
): Promise<Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }>> {
  // Try to use astronomy-calculations.js for maximum accuracy
  try {
    // Ensure astronomy engine is initialized
    if (!astronomyEngine.isReady()) {
      await astronomyEngine.initialize();
    }
    
    if (astronomyEngine.isReady()) {
      const utcDate = new Date(date.toISOString());
      const jd = calculateJulianDay(utcDate);
      
      // Calculate required parameters
      const lst = astronomyEngine.calculateLST(jd, longitude);
      const ramc = lst;
      const tropicalRef = astronomyEngine.calculateTropicalReference(jd);
      const obliquity = tropicalRef.obliquity;
      
      // Calculate ascendant and midheaven
      const ascendant = astronomyEngine.calculateAscendant(ramc, latitude, obliquity);
      const midheaven = astronomyEngine.calculateMidheaven(ramc, obliquity);
      
      // Convert house system name
      let engineSystem: 'placidus' | 'equal' | 'whole_sign' | 'porphyry' = 'placidus';
      if (system === 'equal') engineSystem = 'equal';
      else if (system === 'whole-sign') engineSystem = 'whole_sign';
      else if (system === 'koch') engineSystem = 'placidus'; // Use placidus for koch
      
      // Calculate house cusps using astronomy engine
      const housesData = astronomyEngine.calculateHouseCusps(
        ascendant,
        midheaven,
        latitude,
        obliquity,
        ramc,
        engineSystem
      );
      
      // Convert to expected format
      const houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
      for (let i = 1; i <= 12; i++) {
        const cuspLongitude = housesData.cusps[i];
        const signData = getZodiacSign(cuspLongitude);
        houses.push({
          house: i as House,
          sign: signData.sign as ZodiacSign,
          degree: signData.degree,
          longitude: cuspLongitude
        });
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ House cusps calculated with astronomy-calculations.js:', {
          system: engineSystem,
          ascendant: ascendant.toFixed(2),
          midheaven: midheaven.toFixed(2),
          house1: houses[0].longitude.toFixed(2),
          house10: houses[9].longitude.toFixed(2),
          source: 'astronomy-engine'
        });
      }
      
      return houses;
    } else {
      console.warn('⚠️ Astronomy engine not ready after initialization attempt, using fallback');
    }
  } catch (error) {
    console.warn('Astronomy engine house calculation failed, using fallback:', error);
  }
  
  // Fallback to VSOP87-based calculations
  switch (system) {
    case 'placidus':
      return calculatePlacidusHousesFallback(date, latitude, longitude);
    
    case 'equal':
      const ascendant = await calculateAscendant(date, latitude, longitude);
      return calculateEqualHouses(ascendant.longitude);
    
    case 'whole-sign':
      const asc = await calculateAscendant(date, latitude, longitude);
      return calculateWholeSignHouses(asc.longitude);
    
    case 'koch':
      // Koch is similar to Placidus but uses different calculation method
      // For now, use Placidus as approximation
      return calculatePlacidusHousesFallback(date, latitude, longitude);
    
    default:
      return calculatePlacidusHousesFallback(date, latitude, longitude);
  }
}

/**
 * Determine which house a planet is in
 */
export function getPlanetHouse(
  planetLongitude: number,
  houseCusps: Array<{ house: House; longitude: number }>
): House {
  // Sort houses by longitude
  const sortedHouses = [...houseCusps]
    .map(h => ({ house: h.house, longitude: h.longitude }))
    .sort((a, b) => a.longitude - b.longitude);
  
  // Find which house contains the planet
  for (let i = 0; i < sortedHouses.length; i++) {
    const currentHouse = sortedHouses[i];
    const nextHouse = sortedHouses[(i + 1) % sortedHouses.length];
    
    // Handle wrap-around (when next house is before current)
    if (nextHouse.longitude < currentHouse.longitude) {
      if (planetLongitude >= currentHouse.longitude || planetLongitude < nextHouse.longitude) {
        return currentHouse.house;
      }
    } else {
      if (planetLongitude >= currentHouse.longitude && planetLongitude < nextHouse.longitude) {
        return currentHouse.house;
      }
    }
  }
  
  // Default to house 1 if not found
  return 1;
}

